/* eslint-disable react-hooks/exhaustive-deps */
import React, { useReducer } from 'react';
import authconnect, {
  useAuth,
  useAuthSession,
  useIsAuthenticated,
  useIsLoanOfficer,
} from './authconnect';
import { createStore as createAuthStore } from './Auth';

const AuthContext = React.createContext({});
const defaultState = {
  user: null,
  session: null,
  error: null,
  working: false,
  logout: () => null,
};
const reducer = (state = defaultState, { type, data }) => {
  switch (type) {
    case 'set_working':
      return {
        ...state,
        working: data,
      };
    case 'set_data':
      return {
        ...state,
        ...data,
      };
    default:
      return state;
  }
};

const AuthProvider = ({ store, children }) => {
  const [state, dispatch] = useReducer(reducer, defaultState);
  const [isInitialized, setIsInitialized] = React.useState(false);

  // Use React 19 safe approach - only set dispatch once and in an effect
  React.useEffect(() => {
    // Set dispatch directly on state object (required for Auth class compatibility)
    if (!state.dispatch) {
      state.dispatch = dispatch;
    }

    // Set the Auth instance dispatch to use our local dispatch
    if (store && store.dispatch !== dispatch && !isInitialized) {
      // Set dispatch immediately for immediate availability
      store.dispatch = dispatch;
      store._dispatch = dispatch;

      // Use a more React 19 compatible approach for final setup
      const scheduleAuthInitialization = () => {
        // Use MessageChannel for fully async dispatch assignment
        const channel = new MessageChannel();
        channel.port2.onmessage = () => {
          if (store && typeof store.dispatch === 'function') {
            setIsInitialized(true);
          }
        };
        channel.port1.postMessage(null);
      };

      scheduleAuthInitialization();
    }
  }, [dispatch, store, isInitialized]);

  // Note: Profile fetch is now handled by the App component useEffect to avoid duplicate calls

  // Add error handling for React 19 concurrent rendering issues
  const contextValue = React.useMemo(
    () => ({
      store: {
        dispatch,
        getState: () => {
          // Merge the local state with the Auth instance methods
          return {
            ...state,
            // Include Auth instance methods directly if available
            ...(store && typeof store.login === 'function'
              ? {
                  login: store.login,
                  signup: store.signup, // Add signup method
                  logout: store.logout,
                  handleAuthentication: store.handleAuthentication,
                  isAuthenticated: store.isAuthenticated,
                  getAccessToken: store.getAccessToken,
                  getIdToken: store.getIdToken,
                  refreshToken: store.refreshToken,
                  refreshLock: store.refreshLock,
                  deleteLock: store.deleteLock,
                  startLockClock: store.startLockClock,
                  stopLockClock: store.stopLockClock,
                  initiateProfileFetchIfNeeded:
                    store.initiateProfileFetchIfNeeded,
                  enableProfileFetch: store.enableProfileFetch,
                }
              : {}),
          };
        },
      },
    }),
    [state, dispatch, store],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export {
  authconnect,
  AuthContext,
  AuthProvider,
  createAuthStore,
  useAuth,
  useAuthSession,
  useIsLoanOfficer,
  useIsAuthenticated,
};
